import React, {
  createContext,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ToolbarItem,
  SIDEBAR_TYPE,
  getEligibleProducts,
} from '@amzn/aac-chrome-internal-api';
import {
  CC_MODAL_TOOLBAR_ID,
  CM_TOOLBAR_ID,
  WeblabedFeatures,
} from 'src/commons/constants';
import {
  buildCampaignCreationLinks,
  CAMPAIGN_CREATION_CONFIGS,
} from 'src/commons/utils/helpers';
import { useSidenavBundle } from 'src/providers/PantherLocalizationProvider';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { getNavState } from 'src/commons/apis';

export interface ConsolidatedNavContextProviderProps {
  initToolbarItems: ToolbarItem[];
  updateSelectedNavItem?: (toolbarItemId: string, linkId: string) => void;
  landingAppName?: string;
  updateLandingAppName?: (landingAppName: string) => void;
}

export interface ConsolidatedNavContextType {
  isQuickNavModalEnabled: boolean;
  activeCategoryId: string;
  setActiveCategoryId: React.Dispatch<SetStateAction<string>>;
  isNavOpen: boolean;
  isAllToolsOpen: boolean;
  allToolbarItems: ToolbarItem[];
  setIsNavOpen: React.Dispatch<SetStateAction<boolean>>;
  setIsAllToolsOpen: React.Dispatch<SetStateAction<boolean>>;
  selectedNavItemId: string;
  setSelectedNavItemId: React.Dispatch<SetStateAction<string>>;
  openModalAppName: string;
  setOpenModalAppName: React.Dispatch<SetStateAction<string>>;
  toolbarItems: Array<ToolbarItem>;
  landingAppName?: string;
  updateLandingAppName?: (landingAppName: string) => void;
  updateSelectedNavItem?: (selectedNavItemId: string, linkId: string) => void;
}

export const ConsolidatedNavContext = createContext<ConsolidatedNavContextType>(
  {
    isQuickNavModalEnabled: false,
    activeCategoryId: '',
    setActiveCategoryId: () => '',
    isNavOpen: false,
    isAllToolsOpen: false,
    allToolbarItems: [],
    setIsNavOpen: () => false,
    setIsAllToolsOpen: () => false,
    selectedNavItemId: '',
    setSelectedNavItemId: () => '',
    openModalAppName: '',
    setOpenModalAppName: () => '',
    toolbarItems: [],
    updateSelectedNavItem: () => '',
  },
);

const getSelectedNavItemId = (toolbarItems: ToolbarItem[]) =>
  toolbarItems.find((toolbarItem) =>
    toolbarItem.sidebarGroups?.some((group) =>
      group.links.some((link) => link.isSelected),
    ),
  )?.id ?? '';

export const ConsolidatedNavContextProvider: React.FC<
  ConsolidatedNavContextProviderProps
> = ({
  initToolbarItems,
  updateSelectedNavItem,
  landingAppName,
  updateLandingAppName,
  children,
}) => {
  const bundle = useSidenavBundle();
  const campaignCreationTranslations = useMemo(
    () =>
      new Map(
        CAMPAIGN_CREATION_CONFIGS.map((config) => [
          config.label,
          bundle.getMessage(config.label) || '',
        ]),
      ),
    [bundle],
  );
  const toolbarItems = useMemo<Array<ToolbarItem>>(
    () => initToolbarItems,
    [initToolbarItems],
  );
  const [selectedNavItemId, setSelectedNavItemId] = useState<string>(() =>
    getSelectedNavItemId(initToolbarItems),
  );
  const [isQuickNavModalEnabled, setIsQuickNavModalEnabled] =
    useState<boolean>(false);
  const [openModalAppName, setOpenModalAppName] = useState<string>('');
  const [activeCategoryId, setActiveCategoryId] = useState<string>(() => {
    if (!selectedNavItemId) {
      return '';
    }

    const lookupNavItemId =
      selectedNavItemId === CC_MODAL_TOOLBAR_ID
        ? CM_TOOLBAR_ID
        : selectedNavItemId;

    const toolbarItem = toolbarItems.find(
      (toolbarItem) => toolbarItem.id === lookupNavItemId,
    );
    return toolbarItem &&
      (toolbarItem.sidebarGroups?.length > 1 ||
        toolbarItem.sidebarGroups[0].links.length > 1)
      ? toolbarItem.id
      : '';
  });
  const [isNavOpen, setIsNavOpen] = useState<boolean>(false);
  const [isAllToolsOpen, setIsAllToolsOpen] = useState<boolean>(false);
  const [allToolbarItems, setAllToolbarItems] = useState<ToolbarItem[]>([]);
  const isAdsProductFeatureEnabled = useIsFeatureEnabled(
    WeblabedFeatures.NAV_ADS_PROD,
  );

  useEffect(() => {
    setSelectedNavItemId(getSelectedNavItemId(initToolbarItems));
    const containsQuickNav = toolbarItems.find(
      (toolbarItem) =>
        toolbarItem.sidebarGroups?.[0]?.links?.[0].appName ===
        'CampaignCreationModalFrontEnd',
    );
    setIsQuickNavModalEnabled(!!containsQuickNav);

    const allTools =
      toolbarItems
        .find((item) => item.sidebarType === SIDEBAR_TYPE.ALL_TOOLS)
        ?.allToolbarItems?.filter(
          (toolbarItem) => toolbarItem.sidebarType !== SIDEBAR_TYPE.DIVIDER,
        ) ?? [];

    const hasCCModal = allTools.some((item) => item.id === CC_MODAL_TOOLBAR_ID);

    if (hasCCModal && isAdsProductFeatureEnabled) {
      getNavState()
        .then((navState) => {
          return getEligibleProducts(true, navState?.accountInfo);
        })
        .then((eligibleProductsMap) => {
          if (eligibleProductsMap.size > 0) {
            const campaignCreationLinks = buildCampaignCreationLinks(
              eligibleProductsMap,
              campaignCreationTranslations,
            );
            const updatedAllTools = allTools.map((item) => {
              if (item.id === CC_MODAL_TOOLBAR_ID) {
                return {
                  ...item,
                  sidebarGroups: [{ links: campaignCreationLinks }],
                };
              }
              return item;
            });
            setAllToolbarItems(updatedAllTools);
          } else {
            setAllToolbarItems(allTools);
          }
        });
    } else {
      setAllToolbarItems(allTools);
    }
  }, [toolbarItems]);

  return (
    <ConsolidatedNavContext.Provider
      value={{
        isQuickNavModalEnabled,
        activeCategoryId,
        setActiveCategoryId,
        isNavOpen,
        setIsNavOpen,
        isAllToolsOpen,
        allToolbarItems,
        setIsAllToolsOpen,
        selectedNavItemId,
        setSelectedNavItemId,
        openModalAppName,
        setOpenModalAppName,
        toolbarItems,
        landingAppName,
        updateLandingAppName,
        updateSelectedNavItem,
      }}
    >
      {children}
    </ConsolidatedNavContext.Provider>
  );
};

export const useConsolidatedNavContext = (): ConsolidatedNavContextType => {
  const context: ConsolidatedNavContextType = useContext(
    ConsolidatedNavContext,
  );
  if (context === undefined) {
    throw new Error(
      'useConsolidatedNavContext must be used within ConsolidatedNavContextProvider',
    );
  }
  return context;
};
